<template>
  <div id="dictionary-app" class="container settings-container">
      <ImageBanner
            :settingsPage="true"
            disableAuth
      ></ImageBanner>
    <h2>Dictionary Settings</h2>

    <!-- General Settings -->
    <div class="settings-section">
      <h3>General Settings</h3>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="autoShowFilteredResults"
            @change="saveAutoShowFilteredResults"
            class="setting-checkbox"
          >
          <span class="setting-text">Automatically show hidden results when no visible results found</span>
        </label>
        <p class="setting-description">
          When checked, this will automatically show results from hidden dictionaries when there are no visible results becauese of the dictionary visibility filter settings.
        </p>
      </div>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="showRecentSearchTypes"
            class="setting-checkbox"
          >
          <span class="setting-text">Show the last three used search types</span>
        </label>
        <p class="setting-description">
          Shows the last three most recent search types at the top of the search type drop down for easy access. <br/>({{ searchTypeHistory.length > 0 ? searchTypeHistory.join(', ') : 'No recent searchs yeet' }})
        </p>
      </div>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="showCustomNotes"
            class="setting-checkbox"
          >
          <span class="setting-text">Show the "+ Notes" button to add notes</span>
        </label>
        <p class="setting-description">
          This enables the "+ Notes" button next to each definition, allowing you to add a public custom notes to any term. 
        </p>
      </div>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="showSanskritInDefiniendum"
            class="setting-checkbox"
          >
          <span class="setting-text">Show the Sanskrit term below the Tibetan</span>
        </label>
        <p class="setting-description">
          When enabled, if a Sanskrit equivalent is available for a Tibetan term, it will be displayed below the Tibetan term.
        </p>
      </div>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="showWylieInDefiniendum"
            class="setting-checkbox"
          >
          <span class="setting-text">Show the wylie version of the Tibetan term</span>
        </label>
        <p class="setting-description">
          When enabled, the wylie transliteration of the Tibetan term will be shown in below the Tibetan term.
        </p>
      </div>

      <div class="setting-item">
        <label class="setting-label">
          <input
            type="checkbox"
            v-model="enableAiTranslation"
            @change="saveEnableAiTranslation"
            class="setting-checkbox"
          >
          <span class="setting-text">Enable AI-generated draft translations</span>
        </label>
        <p class="setting-description">
          When enabled, the translation tool will automatically generate a draft English translation using AI. This feature requires an active internet connection and may take a few seconds to generate.
        </p>

        <!-- Quota Status Display -->
        <div v-if="false && quotaStatus" class="quota-status-container">
          <div v-if="!quotaStatus.configured" class="quota-warning">
            ⚠️ Gemini API not configured on server
          </div>
          <div v-else class="quota-status">
            <div class="quota-header">
              <strong>AI Translation Quota</strong>
              <button @click="refreshQuotaStatus" class="btn-refresh" :disabled="quotaLoading">
                {{ quotaLoading ? 'Refreshing...' : 'Refresh' }}
              </button>
            </div>
            <div class="quota-bar-container">
              <div class="quota-bar">
                <div class="quota-used" :style="{ width: quotaPercentUsed + '%' }"></div>
              </div>
              <div class="quota-text">
                {{ quotaStatus.remaining }} / {{ quotaStatus.limit }} requests remaining
                <span v-if="quotaStatus.remaining === 0" class="quota-depleted">
                  (resets at {{ formatResetTime(quotaStatus.resetTime) }})
                </span>
              </div>
            </div>
          </div>
        </div>
        <div v-else-if="quotaError" class="quota-error">
          Failed to load quota status: {{ quotaError }}
        </div>

        <!-- Personal API Key Section -->
        <div class="api-key-section">
          <h4>Personal Gemini API Key (Optional)</h4>
          <p class="api-key-description">
            Add your own Google Gemini API key to bypass the shared 20 requests/day limit.
            Your key is stored locally and sent with each request to override the free tier.
            <a href="https://makersuite.google.com/app/apikey" target="_blank">Get your free API key here</a> Here's a 
            <a href="https://youtu.be/Uyn-P2nRvDA?t=14" target="_blank">YouTube video on how to get your key</a> if you need more help.
          </p>

          <div class="api-key-input-container">
            <div class="api-key-input-wrapper">
              <input
                v-model="localApiKey"
                :type="showApiKey ? 'text' : 'password'"
                class="api-key-input"
                placeholder="Enter your Gemini API key (e.g., AIzaSy...)"
                @input="apiKeyChanged = true"
              >
              <button @click="showApiKey = !showApiKey" class="btn-toggle-visibility" type="button">
                {{ showApiKey ? '👁️' : '👁️‍🗨️' }}
              </button>
            </div>

            <div class="api-key-actions">
              <button
                @click="saveApiKey"
                class="btn-save-key"
                :disabled="!apiKeyChanged || savingApiKey"
              >
                {{ savingApiKey ? 'Saving...' : 'Save Key' }}
              </button>
              <button
                @click="clearApiKey"
                class="btn-clear-key"
                v-if="userGeminiApiKey"
              >
                Clear Key
              </button>
              <button
                @click="testApiKey"
                class="btn-test-key"
                :disabled="!localApiKey || testingApiKey"
                v-if="localApiKey"
              >
                {{ testingApiKey ? 'Testing...' : 'Test Key' }}
              </button>
            </div>

            <div v-if="apiKeyStatus" class="api-key-status" :class="apiKeyStatus.type">
              {{ apiKeyStatus.message }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="settings-section">
      <h3>Dictionary Filtering</h3>
      <p class="settings-description">Choose which dictionaries to include in your search results:</p>

      <table class="dictionary-table">
        <thead>
          <tr>
            <th class="dict-name-header">Dictionary</th>
            <th class="checkbox-header">
              Normal Search
              <div class="header-actions">
                <button @click="selectAllNormal" class="btn-small">All</button>
                <button @click="deselectAllNormal" class="btn-small">None</button>
              </div>
            </th>
            <th class="checkbox-header">
              Translation Tool
              <div class="header-actions">
                <button @click="selectAllTranslation" class="btn-small">All</button>
                <button @click="deselectAllTranslation" class="btn-small">None</button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td class="dict-name">UMA Tibet Dictionary</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.umaTibet" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.umaTibet" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">Yellow Hackett Index</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.hackettIndex" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.hackettIndex" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">Nathan Hill's Verbinator</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.verbinator" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.verbinator" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">Rangjung Yeshe</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.rangjung" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.rangjung" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">Berzin</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.berzin" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.berzin" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">84000</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.eightFourThousand" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.eightFourThousand" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">Tsepak Rigdzin</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.tsepakRigdzin" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.tsepakRigdzin" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">ཚིག་མཛོད་ཆེན་མོ། (Tshig mdzod chen mo)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.tshigMdzod" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.tshigMdzod" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">དུང་དཀར་ཚིག་མཛོད་ཆེན་མོ། (Dung dkar)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.dungDkar" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.dungDkar" @change="saveFilters">
            </td>
          </tr>

          <tr>
            <td class="dict-name">དག་ཚིག་གསར་བསྒྲིགས། (Dag tshig)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="normalSearchFilters.dagTshig" @change="saveFilters">
            </td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="translationToolFilters.dagTshig" @change="saveFilters">
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="settings-section">
      <h3>Search Types</h3>
      <p class="settings-description">Choose which search types to show in the dropdown:</p>

      <table class="search-types-table">
        <thead>
          <tr>
            <th class="search-type-header">Search Type</th>
            <th class="checkbox-header">
              Enabled
              <div class="header-actions">
                <button @click="selectAllSearchTypes" class="btn-small">All</button>
                <button @click="deselectAllSearchTypes" class="btn-small">None</button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
            <tr class="divider-row">
            <td colspan="2" class="divider-cell">
              <div class="divider-line"></div>
              <span class="divider-text">Classical</span>
              <div class="divider-line"></div>
            </td>
          </tr>
          <tr>
            <td class="search-type-name">Full Text</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.fulltext" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Full Text (all fields)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['fulltext-all-fields']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">English/Definition</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.englishDefinition" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Definiendum (contains)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.definiendum" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Definiendum (begins with)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.definiendumStarts" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Definiendum (exact)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.definiendumExact" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Definiendum (LIKE)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.definiendumLike" @change="saveSearchTypes">
            </td>
          </tr>

          <tr v-tooltip="`This is currently disabled`">
            <td class="search-type-name">Definiendum (regex)</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes.definiendumRegex" @change="saveSearchTypes" disabled>
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Translation Tool</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['translation-tool']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">Verb Tense</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['verb-tense']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr class="divider-row">
            <td colspan="2" class="divider-cell">
              <div class="divider-line"></div>
              <span class="divider-text">Colloquial / Monlam</span>
              <div class="divider-line"></div>
            </td>
          </tr>

          <tr>
            <td class="search-type-name">English to བོད་</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['monlam-englishToTibetan']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">བོད་ to English</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['monlam-tibetanToEnglish']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">སྨོན་ལམ་ཚིག་མཛོད་ཆེན་མོ། 2021</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['monlam-tibetanToTibetan']" @change="saveSearchTypes">
            </td>
          </tr>

          <tr>
            <td class="search-type-name">སྨོན་ལམ་ཚིག་མཛོད་ཆེན་མོ། 2016</td>
            <td class="checkbox-cell">
              <input type="checkbox" v-model="enabledSearchTypes['monlam-tibetanToTibetanOld']" @change="saveSearchTypes">
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div class="settings-section">
      <h3>Translation Tool Ignore List</h3>
      <p class="settings-description">Control which terms are hidden from translation tool results.</p>
      <p class="settings-description">This is useful for filtering out common particles or other words that you want to ignore when using the translation tool. Any terms listed here
        will be filtered out of the translation tool results to help you focus on the words you care about.</p>

      <!-- Standard Particles Toggle -->
      <div class="standard-particles-container">
        <div class="toggle-header">
          <label class="toggle-label">
            <input
              type="checkbox"
              v-model="ignoreStandardParticles"
              @change="saveIgnoreStandardParticles"
              class="toggle-checkbox"
            >
            <span class="toggle-text">Ignore Standard Particles</span>
          </label>
        </div>

        <div v-if="ignoreStandardParticles" class="particles-categories-section">
          <div class="toggle-all-container">
            <button @click="toggleAllCategories(true)" class="btn-toggle-all">Enable All</button>
            <button @click="toggleAllCategories(false)" class="btn-toggle-all">Disable All</button>
          </div>

          <div v-for="category in standardParticles" :key="category.id" class="particle-category">
            <div class="category-header">
              <label class="category-toggle-label">
                <input
                  type="checkbox"
                  :checked="particleCategoriesEnabled[category.id]"
                  @change="toggleCategory(category.id, $event.target.checked)"
                  class="category-checkbox"
                >
                <div class="category-info">
                  <span class="category-name">{{ category.name }}</span>
                  <span class="category-description">{{ category.description }}</span>
                </div>
              </label>
            </div>
            <div class="particles-grid" :class="{ 'disabled-category': !particleCategoriesEnabled[category.id] }">
              <span v-for="particle in category.particles" :key="particle" class="particle">{{ particle }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Custom Terms Input -->
      <div class="add-terms-container">
        <h4 style="margin-top: 0; margin-bottom: 15px;">Custom Terms</h4>
        <label for="terms-input">Add custom terms to ignore (comma-separated):</label>
        <div class="input-button-group">
          <textarea
            id="terms-input"
            v-model="newTermsInput"
            placeholder="བོད་, སངས་རྒྱས་, བླ་མ་, ..."
            rows="2"
            class="terms-input"
          ></textarea>
          <button @click="addTermsFromInput" class="btn-add-terms">Add Terms</button>
        </div>
        <div v-if="addTermsError" class="error-message">{{ addTermsError }}</div>
        <div v-if="addTermsSuccess" class="success-message">{{ addTermsSuccess }}</div>
      </div>

      <div v-if="translationToolIgnoreList.length === 0" class="empty-list-message">
        No terms are currently hidden from translation tool results.
      </div>

      <table v-else class="ignore-list-table">
        <thead>
          <tr>
            <th class="term-header">Tibetan Term</th>
            <th class="action-header">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(term, index) in translationToolIgnoreList" :key="index">
            <td class="term-cell" v-html="term"></td>
            <td class="action-cell">
              <button @click="removeTerm(term)" class="btn-remove">Remove</button>
            </td>
          </tr>
        </tbody>
      </table>

      <div v-if="translationToolIgnoreList.length > 0" class="clear-all-container">
        <button @click="clearAllTerms" class="btn-clear-all">Clear All</button>
      </div>
    </div>

    <div class="settings-footer">
      <button @click="resetAllPreferences" class="btn-reset">Reset All Preferences to Default</button>
      <button @click="goBackToDictionary" class="btn-primary">Back to Dictionary</button>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from 'vuex'
import axios from 'axios'
import ImageBanner from '../components/ImageBanner';


export default {
  name: 'SettingsView',
  components: {
    ImageBanner
  },
  computed: {
    ...mapState('app', ['translationToolIgnoreList', 'searchTypeHistory']),
    ignoreStandardParticles: {
      get() {
        return this.$store.state.app.ignoreStandardParticles;
      },
      set(value) {
        this.$store.commit('app/setIgnoreStandardParticles', value);
      }
    },
    autoShowFilteredResults: {
      get() {
        return this.$store.state.app.autoShowFilteredResults;
      },
      set(value) {
        this.$store.commit('app/setAutoShowFilteredResults', value);
      }
    },
    showCustomNotes: {
      get() {
        return this.$store.state.app.showCustomNotes;
      },
      set(value) {
        this.$store.commit('app/setShowCustomNotes', value);
      }
    },
    showRecentSearchTypes: {
      get() {
        return this.$store.state.app.showRecentSearchTypes;
      },
      set(value) {
        this.$store.commit('app/setShowRecentSearchTypes', value);
      }
    },
    showWylieInDefiniendum: {
      get() {
        return this.$store.state.app.showWylieInDefiniendum;
      },
      set(value) {
        this.$store.commit('app/setShowWylieInDefiniendum', value);
      }
    },
    showSanskritInDefiniendum: {
      get() {
        return this.$store.state.app.showSanskritInDefiniendum;
      },
      set(value) {
        this.$store.commit('app/setShowSanskritInDefiniendum', value);
      }
    },
    enableAiTranslation: {
      get() {
        return this.$store.state.app.enableAiTranslation;
      },
      set(value) {
        this.$store.commit('app/setEnableAiTranslation', value);
      }
    },
    quotaPercentUsed() {
      if (!this.quotaStatus || !this.quotaStatus.limit) return 0;
      return (this.quotaStatus.used / this.quotaStatus.limit) * 100;
    },
    userGeminiApiKey() {
      return this.$store.state.app.userGeminiApiKey;
    },
    standardParticles() {
      return this.$store.state.app.standardParticles;
    },
    particleCategoriesEnabled() {
      return this.$store.state.app.particleCategoriesEnabled;
    }
  },
  data() {
    return {
      previousQuery: {},
      newTermsInput: '',
      addTermsError: '',
      addTermsSuccess: '',
      quotaStatus: null,
      quotaError: null,
      quotaLoading: false,
      localApiKey: '',
      showApiKey: false,
      apiKeyChanged: false,
      savingApiKey: false,
      testingApiKey: false,
      apiKeyStatus: null,
      normalSearchFilters: {
        umaTibet: true,
        hackettIndex: true,
        verbinator: true,
        rangjung: true,
        berzin: true,
        eightFourThousand: true,
        tshigMdzod: true,
        dungDkar: true,
        dagTshig: true,
        tsepakRigdzin: true
      },
      translationToolFilters: {
        umaTibet: true,
        hackettIndex: true,
        verbinator: true,
        rangjung: true,
        berzin: true,
        eightFourThousand: true,
        tshigMdzod: true,
        dungDkar: true,
        dagTshig: true,
        tsepakRigdzin: true
      },
      enabledSearchTypes: {
        fulltext: true,
        'fulltext-all-fields': true,
        definiendum: true,
        englishDefinition: true,
        definiendumStarts: true,
        definiendumExact: true,
        definiendumLike: false,
        definiendumRegex: false,
        'translation-tool': true,
        'verb-tense': true,
        'monlam-englishToTibetan': true,
        'monlam-tibetanToEnglish': true,
        'monlam-tibetanToTibetan': true,
        'monlam-tibetanToTibetanOld': false
      }
    }
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      console.log('Entering settings with query:', JSON.stringify(from.query));
      vm.previousQuery = from.query || {};
    });
  },
  mounted() {
    this.loadFilters();
    this.loadSearchTypes();
    this.loadQuotaStatus();
    this.loadApiKey();
  },
  methods: {
    ...mapMutations([
      'setDictionaryFilters',
      'setEnabledSearchTypes'
    ]),
    goBackToDictionary() {
      this.$router.push({ path: '/'});
    },
    loadFilters() {
      // Load from Vuex store
      const filters = this.$store.state.app.dictionaryFilters;
      if (filters) {
        if (filters.normalSearch) {
          this.normalSearchFilters = { ...filters.normalSearch };
        }
        if (filters.translationTool) {
          this.translationToolFilters = { ...filters.translationTool };
        }
      }
    },
    loadSearchTypes() {
      // Load enabled search types from Vuex store
      const searchTypes = this.$store.state.app.enabledSearchTypes;
      if (searchTypes) {
        this.enabledSearchTypes = { ...searchTypes };
      }
    },
    saveFilters() {
      const filters = {
        normalSearch: { ...this.normalSearchFilters },
        translationTool: { ...this.translationToolFilters }
      };
      this.$store.commit('app/setDictionaryFilters', filters);
    },
    selectAllNormal() {
      Object.keys(this.normalSearchFilters).forEach(key => {
        this.normalSearchFilters[key] = true;
      });
      this.saveFilters();
    },
    deselectAllNormal() {
      Object.keys(this.normalSearchFilters).forEach(key => {
        this.normalSearchFilters[key] = false;
      });
      this.saveFilters();
    },
    selectAllTranslation() {
      Object.keys(this.translationToolFilters).forEach(key => {
        this.translationToolFilters[key] = true;
      });
      this.saveFilters();
    },
    deselectAllTranslation() {
      Object.keys(this.translationToolFilters).forEach(key => {
        this.translationToolFilters[key] = false;
      });
      this.saveFilters();
    },
    saveSearchTypes() {
      this.$store.commit('app/setEnabledSearchTypes', { ...this.enabledSearchTypes });
    },
    selectAllSearchTypes() {
      Object.keys(this.enabledSearchTypes).forEach(key => {
        this.enabledSearchTypes[key] = true;
      });
      this.saveSearchTypes();
    },
    deselectAllSearchTypes() {
      Object.keys(this.enabledSearchTypes).forEach(key => {
        this.enabledSearchTypes[key] = false;
      });
      // Always keep at least fulltext enabled
      this.enabledSearchTypes.fulltext = true;
      this.saveSearchTypes();
    },
    saveIgnoreStandardParticles() {
      // The v-model binding automatically saves to Vuex and localStorage
      // This method is just here for the @change event if we need additional logic later
    },
    toggleCategory(categoryId, enabled) {
      this.$store.commit('app/setParticleCategoryEnabled', { categoryId, enabled });
    },
    toggleAllCategories(enabled) {
      this.standardParticles.forEach(category => {
        this.$store.commit('app/setParticleCategoryEnabled', { categoryId: category.id, enabled });
      });
    },
    saveAutoShowFilteredResults() {
      // The v-model binding automatically saves to Vuex and localStorage
      // This method is just here for the @change event if we need additional logic later
    },
    saveEnableAiTranslation() {
      // The v-model binding automatically saves to Vuex and localStorage
      // This method is just here for the @change event if we need additional logic later
    },
    async loadQuotaStatus() {
      this.quotaLoading = true;
      this.quotaError = null;

      try {
        const response = await axios.get(this.$server.apiUrl + '/api/gemini/status');

        if (response.data) {
          this.quotaStatus = {
            configured: response.data.configured,
            available: response.data.available,
            used: response.data.quota ? response.data.quota.used : 0,
            remaining: response.data.quota ? response.data.quota.remaining : 0,
            limit: response.data.quota ? response.data.quota.limit : 20,
            resetTime: response.data.quota ? response.data.quota.resetTime : null
          };
        }
      } catch (error) {
        console.error('Failed to load quota status:', error);
        this.quotaError = 'Unable to connect to server';
      } finally {
        this.quotaLoading = false;
      }
    },
    async refreshQuotaStatus() {
      await this.loadQuotaStatus();
    },
    formatResetTime(resetTime) {
      if (!resetTime) return 'Unknown';
      const date = new Date(resetTime);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    },
    loadApiKey() {
      // Load from Vuex store (which loads from localStorage)
      if (this.userGeminiApiKey) {
        this.localApiKey = this.userGeminiApiKey;
      }
    },
    saveApiKey() {
      this.savingApiKey = true;
      this.apiKeyStatus = null;

      try {
        const trimmedKey = this.localApiKey.trim();

        if (trimmedKey) {
          this.$store.commit('app/setUserGeminiApiKey', trimmedKey);
          this.apiKeyStatus = {
            type: 'success',
            message: '✓ API key saved successfully! Your personal key will be used for AI translations.'
          };
        } else {
          this.$store.commit('app/setUserGeminiApiKey', null);
          this.apiKeyStatus = {
            type: 'info',
            message: 'API key cleared. Using shared free tier.'
          };
        }

        this.apiKeyChanged = false;
      } catch (error) {
        console.error('Failed to save API key:', error);
        this.apiKeyStatus = {
          type: 'error',
          message: '✗ Failed to save API key. Please try again.'
        };
      } finally {
        this.savingApiKey = false;
      }
    },
    clearApiKey() {
      this.localApiKey = '';
      this.$store.commit('app/setUserGeminiApiKey', null);
      this.apiKeyChanged = false;
      this.apiKeyStatus = {
        type: 'info',
        message: 'API key removed. Using shared free tier (20 requests/day).'
      };
    },
    async testApiKey() {
      this.testingApiKey = true;
      this.apiKeyStatus = null;

      try {
        const response = await axios.post(this.$server.apiUrl + '/api/gemini/query', {
          prompt: 'Say "API key is working" in exactly those words.',
          options: {
            temperature: 0,
            maxOutputTokens: 20
          },
          userApiKey: this.localApiKey.trim()
        });

        if (response.data && response.data.text) {
          this.apiKeyStatus = {
            type: 'success',
            message: '✓ API key is valid and working! Response received from Gemini.'
          };
        } else {
          this.apiKeyStatus = {
            type: 'error',
            message: '✗ Unexpected response from API. Key may be invalid.'
          };
        }
      } catch (error) {
        console.error('API key test failed:', error);

        if (error.response && error.response.status === 401) {
          this.apiKeyStatus = {
            type: 'error',
            message: '✗ Invalid API key. Please check your key and try again.'
          };
        } else if (error.response && error.response.data && error.response.data.error) {
          this.apiKeyStatus = {
            type: 'error',
            message: `✗ ${error.response.data.error}`
          };
        } else {
          this.apiKeyStatus = {
            type: 'error',
            message: '✗ Failed to test API key. Please check your connection.'
          };
        }
      } finally {
        this.testingApiKey = false;
      }
    },
    addTermsFromInput() {
      // Clear previous messages
      this.addTermsError = '';
      this.addTermsSuccess = '';

      if (!this.newTermsInput.trim()) {
        this.addTermsError = 'Please enter at least one term.';
        return;
      }

      // Validate that input contains only Tibetan characters, commas, and whitespace
      const tibetanAndCommaRegex = /^[\u0F00-\u0FFF\s,]+$/;
      if (!tibetanAndCommaRegex.test(this.newTermsInput)) {
        this.addTermsError = 'Please enter only Tibetan characters. Non-Tibetan characters are not allowed.';
        return;
      }

      // Split by comma and process each term
      const terms = this.newTermsInput
        .split(',')
        .map(term => term.trim())
        .filter(term => term.length > 0);

      if (terms.length === 0) {
        this.addTermsError = 'No valid terms found. Please enter terms separated by commas.';
        return;
      }

      // Add each term to the ignore list
      let addedCount = 0;
      let skippedCount = 0;

      terms.forEach(term => {
        if (!this.$store.state.app.translationToolIgnoreList.includes(term)) {
          this.$store.commit('app/addToTranslationToolIgnoreList', term);
          addedCount++;
        } else {
          skippedCount++;
        }
      });

      // Show success message
      if (addedCount > 0) {
        this.addTermsSuccess = `Added ${addedCount} term${addedCount > 1 ? 's' : ''} to ignore list.`;
        if (skippedCount > 0) {
          this.addTermsSuccess += ` (${skippedCount} already in list)`;
        }
      } else {
        this.addTermsError = 'All terms are already in the ignore list.';
      }

      // Clear input
      this.newTermsInput = '';

      // Clear success message after 3 seconds
      setTimeout(() => {
        this.addTermsSuccess = '';
      }, 3000);
    },
    removeTerm(term) {
      this.$store.commit('app/removeFromTranslationToolIgnoreList', term);
    },
    clearAllTerms() {
      if (confirm('Are you sure you want to remove all terms from the ignore list?')) {
        // Remove all terms one by one
        const terms = [...this.$store.state.app.translationToolIgnoreList];
        terms.forEach(term => {
          this.$store.commit('app/removeFromTranslationToolIgnoreList', term);
        });
      }
    },
    resetAllPreferences() {
      if (confirm('Are you sure you want to reset all preferences to their default values? This will:\n\n• Reset all dictionary filters\n• Reset search type preferences\n• Clear the translation tool ignore list\n• Reset all display preferences\n• Clear particle filter settings\n\nYour login and API key will NOT be affected.')) {
        this.$store.commit('app/resetPreferences');

        // Reload local data to reflect changes
        this.loadFilters();
        this.loadSearchTypes();

        alert('All preferences have been reset to default values.');
      }
    }
  }
}
</script>

<style scoped>
.settings-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.settings-header {
  text-align: left;
  margin: 20px 0;
}

h2 {
  color: #333;
  margin-bottom: 30px;
  text-align: center;
}

.settings-section {
  background: #f9f9f9;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 30px;
}

.settings-section h3 {
  color: #555;
  margin-top: 0;
  margin-bottom: 15px;
}

/* General Settings */
.setting-item {
  background: white;
  padding: 15px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.setting-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 500;
  color: #333;
}

.setting-checkbox {
  width: 18px;
  height: 18px;
  margin-right: 10px;
  cursor: pointer;
}

.setting-text {
  font-size: 15px;
}

.setting-description {
  margin: 10px 0 0 28px;
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}

.settings-description {
  color: #666;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.dictionary-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.dictionary-table thead {
  background-color: #f5f5f5;
}

.dictionary-table th {
  padding: 15px;
  text-align: center;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.dict-name-header {
  width: 50%;
}

.checkbox-header {
  width: 25%;
  text-align: center;
}

.header-actions {
  margin-top: 8px;
  display: flex;
  gap: 5px;
  justify-content: center;
}

.btn-small {
  padding: 2px 8px;
  font-size: 12px;
  background-color: #e0e0e0;
  border: none;
  border-radius: 3px;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-small:hover {
  background-color: #d0d0d0;
}

.dictionary-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.dictionary-table tbody tr:hover {
  background-color: #f9f9f9;
}

.dictionary-table td {
  padding: 12px 15px;
}

.dict-name {
  font-size: 15px;
  color: #333;
}

.checkbox-cell {
  text-align: center;
}

.checkbox-cell input[type="checkbox"] {
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.search-types-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.search-types-table thead {
  background-color: #f5f5f5;
}

.search-types-table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.search-type-header {
  width: 75%;
}

.search-types-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.search-types-table tbody tr:hover {
  background-color: #f9f9f9;
}

.search-types-table td {
  padding: 12px 15px;
}

.search-type-name {
  font-size: 15px;
  color: #333;
}

.divider-row {
  background: transparent !important;
}

.divider-row:hover {
  background: transparent !important;
}

.divider-cell {
  padding: 20px 15px !important;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 15px;
}

.divider-line {
  flex: 1;
  height: 1px;
  background-color: #d0d0d0;
}

.divider-text {
  color: #666;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  white-space: nowrap;
}

.settings-footer {
  text-align: center;
  margin-top: 40px;
  display: flex;
  gap: 15px;
  justify-content: center;
  flex-wrap: wrap;
}

.btn-primary {
  padding: 12px 24px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #45a049;
}

.btn-reset {
  padding: 12px 24px;
  background-color: #ff9800;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-reset:hover {
  background-color: #f57c00;
}

/* Translation Tool Ignore List styles */
.add-terms-container {
  margin-bottom: 25px;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.add-terms-container label {
  display: block;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
}

/* Standard Particles Toggle */
.standard-particles-container {
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 25px;
}

.toggle-header {
  margin-bottom: 15px;
}

.toggle-label {
  display: flex;
  align-items: center;
  cursor: pointer;
  font-weight: 600;
  color: #333;
}

.toggle-checkbox {
  width: 20px;
  height: 20px;
  margin-right: 10px;
  cursor: pointer;
}

.toggle-text {
  font-size: 16px;
}

.particles-list-box {
  background: #f9f9f9;
  padding: 15px;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.particles-list-title {
  font-size: 14px;
  color: #666;
  margin: 0 0 10px 0;
  font-weight: 500;
}

.particles-categories-section {
  margin-top: 20px;
  padding: 15px;
  background: #f9f9f9;
  border-radius: 8px;
}

.toggle-all-container {
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  justify-content: flex-end;
}

.btn-toggle-all {
  padding: 8px 16px;
  background-color: #2196F3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: background-color 0.2s;
}

.btn-toggle-all:hover {
  background-color: #1976D2;
}

.particle-category {
  margin-bottom: 25px;
  padding: 15px;
  background: white;
  border-radius: 6px;
  border: 1px solid #e0e0e0;
}

.category-header {
  margin-bottom: 12px;
}

.category-toggle-label {
  display: flex;
  align-items: flex-start;
  cursor: pointer;
  gap: 10px;
}

.category-checkbox {
  margin-top: 3px;
  width: 18px;
  height: 18px;
  cursor: pointer;
}

.category-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.category-name {
  font-weight: 600;
  font-size: 16px;
  color: #333;
}

.category-description {
  font-size: 13px;
  color: #666;
  font-style: italic;
}

.particles-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50px, 1fr));
  gap: 8px;
  transition: opacity 0.2s;
}

.particles-grid.disabled-category {
  opacity: 0.3;
}

.particle {
  background: white;
  padding: 6px 10px;
  border-radius: 4px;
  text-align: center;
  font-size: 18px;
  border: 1px solid #ddd;
  color: #333;
}

.input-button-group {
  display: flex;
  gap: 10px;
  align-items: flex-start;
}

.terms-input {
  flex: 1;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 16px;
  font-family: inherit;
  resize: vertical;
  min-height: 60px;
}

.terms-input:focus {
  outline: none;
  border-color: #4CAF50;
  box-shadow: 0 0 0 2px rgba(76, 175, 80, 0.1);
}

.btn-add-terms {
  padding: 10px 20px;
  background-color: #4CAF50;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: background-color 0.2s;
  white-space: nowrap;
}

.btn-add-terms:hover {
  background-color: #45a049;
}

.error-message {
  margin-top: 10px;
  padding: 10px;
  background-color: #fee;
  color: #c33;
  border-radius: 4px;
  font-size: 14px;
}

.success-message {
  margin-top: 10px;
  padding: 10px;
  background-color: #efe;
  color: #3a3;
  border-radius: 4px;
  font-size: 14px;
}

.empty-list-message {
  text-align: center;
  padding: 30px;
  color: #999;
  font-style: italic;
}

.ignore-list-table {
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.ignore-list-table thead {
  background-color: #f5f5f5;
}

.ignore-list-table th {
  padding: 15px;
  text-align: left;
  font-weight: 600;
  color: #333;
  border-bottom: 2px solid #e0e0e0;
}

.term-header {
  width: 70%;
}

.action-header {
  width: 30%;
  text-align: center;
}

.ignore-list-table tbody tr {
  border-bottom: 1px solid #f0f0f0;
  transition: background-color 0.2s;
}

.ignore-list-table tbody tr:hover {
  background-color: #f9f9f9;
}

.ignore-list-table td {
  padding: 12px 15px;
}

.term-cell {
  font-size: 18px;
  color: #333;
}

.action-cell {
  text-align: center;
}

.btn-remove {
  padding: 6px 16px;
  background-color: #dc3545;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-remove:hover {
  background-color: #c82333;
}

.clear-all-container {
  margin-top: 20px;
  text-align: center;
}

.btn-clear-all {
  padding: 10px 24px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.2s;
}

.btn-clear-all:hover {
  background-color: #5a6268;
}

/* Quota Status Styles */
.quota-status-container {
  margin-top: 15px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.quota-warning {
  color: #ff5722;
  padding: 12px;
  background: #ffebee;
  border-radius: 6px;
  text-align: center;
  font-weight: 500;
}

.quota-status {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.quota-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.btn-refresh {
  padding: 6px 12px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 13px;
  transition: background-color 0.2s;
}

.btn-refresh:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-refresh:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.quota-bar-container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.quota-bar {
  height: 24px;
  background: #e0e0e0;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);
}

.quota-used {
  height: 100%;
  background: linear-gradient(90deg, #4CAF50, #8BC34A);
  transition: width 0.3s ease;
  border-radius: 12px;
}

.quota-text {
  font-size: 14px;
  color: #495057;
  text-align: center;
}

.quota-depleted {
  color: #dc3545;
  font-weight: 500;
}

.quota-error {
  color: #dc3545;
  padding: 12px;
  background: #f8d7da;
  border-radius: 6px;
  margin-top: 15px;
  text-align: center;
}

/* API Key Section Styles */
.api-key-section {
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #dee2e6;
}

.api-key-section h4 {
  margin-bottom: 10px;
  color: #495057;
}

.api-key-description {
  font-size: 14px;
  color: #6c757d;
  margin-bottom: 15px;
  line-height: 1.5;
}

.api-key-description a {
  color: #007bff;
  text-decoration: underline;
}

.api-key-input-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.api-key-input-wrapper {
  display: flex;
  gap: 8px;
  align-items: stretch;
}

.api-key-input {
  flex: 1;
  padding: 10px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
  font-family: 'Courier New', monospace;
}

.api-key-input:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-toggle-visibility {
  padding: 8px 12px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 18px;
  transition: background-color 0.2s;
}

.btn-toggle-visibility:hover {
  background-color: #5a6268;
}

.api-key-actions {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.btn-save-key,
.btn-clear-key,
.btn-test-key {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s;
}

.btn-save-key {
  background-color: #28a745;
  color: white;
}

.btn-save-key:hover:not(:disabled) {
  background-color: #218838;
}

.btn-save-key:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.btn-clear-key {
  background-color: #dc3545;
  color: white;
}

.btn-clear-key:hover {
  background-color: #c82333;
}

.btn-test-key {
  background-color: #17a2b8;
  color: white;
}

.btn-test-key:hover:not(:disabled) {
  background-color: #138496;
}

.btn-test-key:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

.api-key-status {
  padding: 12px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
}

.api-key-status.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.api-key-status.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.api-key-status.info {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}
</style>